'use client';

import { type RefCallback, useCallback, useEffect, useRef } from 'react';
import { usePosthog } from '@/hooks/use-posthog';
import type { AppliedJob } from '@/services/application';
import type {
  JobDetail,
  JobFilterOption,
  JobFilters,
  JobFilterState,
  JobSummary,
} from '@/services/jobs/model';
import {
  getApplicationTrackingInfo,
  getOpportunityTrackingInfo,
  getTrackingUserInfo,
  OPPORTUNITY_TRACKING_EVENTS,
  type OpportunityTrackingEventName,
  type SearchMethod,
  type TrackingInfo,
} from '@/services/opportunity-tracking';
import {
  getWebsiteTrackingScreen,
  SOURCE_SCREENS,
  type OpportunityDetailSource,
  type SourceScreen,
} from '@/services/opportunity-tracking/source-screen';

type TrackOpportunityEventOptions = {
  applicationId?: string;
  eventName: OpportunityTrackingEventName;
  includeUserInfo?: boolean;
  jobId?: string;
  properties?: TrackingInfo;
};

type TrackingInfoRequirements = {
  application: boolean;
  opportunity: boolean;
  user: boolean;
};

const TRACKING_INFO_REQUIREMENTS: Record<
  OpportunityTrackingEventName,
  TrackingInfoRequirements
> = {
  [OPPORTUNITY_TRACKING_EVENTS.applicationCreatedSuccess]: {
    application: false,
    opportunity: false,
    user: false,
  },
  [OPPORTUNITY_TRACKING_EVENTS.applyFail]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.applyOpportunity]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.applyStart]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.applySuccess]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.askApplicationResultFail]: {
    application: true,
    opportunity: false,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.askApplicationResultSuccess]: {
    application: true,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.askResultButtonImpression]: {
    application: true,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.confirmAskApplicationResult]: {
    application: true,
    opportunity: false,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.filter]: {
    application: false,
    opportunity: false,
    user: false,
  },
  [OPPORTUNITY_TRACKING_EVENTS.impression]: {
    application: false,
    opportunity: true,
    user: false,
  },
  [OPPORTUNITY_TRACKING_EVENTS.markApplicationReplied]: {
    application: true,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.save]: {
    application: false,
    opportunity: true,
    user: false,
  },
  [OPPORTUNITY_TRACKING_EVENTS.search]: {
    application: false,
    opportunity: false,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.share]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.viewApplicationConfirmation]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.viewApplicationStatus]: {
    application: false,
    opportunity: false,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.viewBottomSheet]: {
    application: true,
    opportunity: false,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.viewDetail]: {
    application: false,
    opportunity: true,
    user: true,
  },
  [OPPORTUNITY_TRACKING_EVENTS.viewResult]: {
    application: false,
    opportunity: false,
    user: true,
  },
};

const TRACKING_PROPERTY_NAMES: Record<
  OpportunityTrackingEventName,
  ReadonlySet<string>
> = {
  [OPPORTUNITY_TRACKING_EVENTS.applicationCreatedSuccess]: new Set([
    'platform',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.applyFail]: new Set([
    'error_code',
    'matching_percentage',
    'number_of_attachment',
    'opportunity_type',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.applyOpportunity]: new Set([
    'matching_percentage',
    'number_of_attachment',
    'opportunity_type',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.applyStart]: new Set([
    'has_few_applicant',
    'has_quick_response_badge',
    'is_new_job',
    'matching_percentage',
    'number_of_days_on_quick_response_badge',
    'number_of_missing_requirement',
    'opportunity_type',
    'quick_response_badge_type',
    'source_screen',
    'text_on_applicant_badge',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.applySuccess]: new Set([
    'cv_attachment',
    'has_few_applicant',
    'is_new_job',
    'matching_percentage',
    'opportunity_type',
    'source_screen',
    'text_on_applicant_badge',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.askApplicationResultFail]: new Set([
    'application_latest_status',
    'job_source',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.askApplicationResultSuccess]: new Set([
    'application_latest_status',
    'job_source',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.askResultButtonImpression]: new Set([
    'application_latest_status',
    'ask_button_status',
    'job_source',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.confirmAskApplicationResult]: new Set([
    'application_latest_status',
    'job_source',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.filter]: new Set([
    'filter_few_applicant',
    'filter_level',
    'filter_location',
    'filter_new_job',
    'filter_salary',
    'filter_weekend_holiday',
    'filter_working_arrangement',
    'filter_working_type',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.impression]: new Set([
    'has_quick_response_badge',
    'is_salary_hidden',
    'job_level',
    'job_title',
    'location',
    'matching_percentage',
    'number_of_days_on_quick_response_badge',
    'opportunity_type',
    'quick_response_badge_type',
    'search_method',
    'search_query',
    'source_screen',
    'source_section',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.markApplicationReplied]: new Set([
    'application_latest_status',
    'job_source',
    'other_outcome_text',
    'reply_outcome',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.save]: new Set([
    'library',
    'matching_percentage',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.search]: new Set([
    'search_method',
    'search_query',
    'source_screen',
    'source_section',
    'suggested_keyword',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.share]: new Set([
    'opportunity_type',
    'share_channel',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.viewApplicationConfirmation]: new Set([
    'matching_percentage',
    'number_of_attachment',
    'opportunity_type',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.viewApplicationStatus]: new Set([
    'application_count',
    'pending_application_count',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.viewBottomSheet]: new Set([
    'application_latest_status',
    'bottom_sheet_name',
    'source_screen',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.viewDetail]: new Set([
    'has_few_applicant',
    'has_hiring_line',
    'has_profile_gap_result',
    'has_profile_gap_trigger_button',
    'has_quick_response_badge',
    'hiring_line_category',
    'hiring_line_name',
    'is_new_job',
    'matching_percentage',
    'number_of_days_on_quick_response_badge',
    'number_of_half_matching_requirement',
    'number_of_matching_requirement',
    'number_of_missing_requirement',
    'opportunity_type',
    'profile_gap_button_type',
    'quick_response_badge_type',
    'source_screen',
    'source_section',
    'text_on_applicant_badge',
  ]),
  [OPPORTUNITY_TRACKING_EVENTS.viewResult]: new Set([
    'company_info',
    'error_code',
    'loading_time',
    'number_of_result',
    'search_method',
    'search_query',
    'source_screen',
  ]),
};

const trackedImpressions = new Set<string>();
const trackedDetails = new Set<string>();
const trackedAskResultButtonImpressions = new Set<string>();
const MAX_TRACKED_EVENT_KEYS = 1000;
const NEW_JOB_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const NO_PROFILE_GAP_MATCHING_PERCENTAGE = -1;
const fullyVisibleHandlers = new WeakMap<
  Element,
  (isFullyVisible: boolean) => void
>();

let fullyVisibleObserver: IntersectionObserver | null = null;
let fullyVisibleObserverTargetCount = 0;

function observeFullyVisible(
  element: Element,
  onVisibilityChange: (isFullyVisible: boolean) => void
) {
  if (!fullyVisibleObserver) {
    fullyVisibleObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          fullyVisibleHandlers.get(entry.target)?.(
            entry.isIntersecting && entry.intersectionRatio >= 0.99
          );
        }
      },
      { threshold: [0.99, 1] }
    );
  }

  const observer = fullyVisibleObserver;

  fullyVisibleHandlers.set(element, onVisibilityChange);
  fullyVisibleObserverTargetCount += 1;
  observer.observe(element);

  let isObserving = true;

  return () => {
    if (!isObserving) return;

    isObserving = false;
    fullyVisibleHandlers.delete(element);
    observer.unobserve(element);
    fullyVisibleObserverTargetCount -= 1;

    if (fullyVisibleObserverTargetCount === 0) {
      observer.disconnect();

      if (fullyVisibleObserver === observer) {
        fullyVisibleObserver = null;
      }
    }
  };
}

function addBoundedTrackingKey(keys: Set<string>, key: string) {
  keys.add(key);

  if (keys.size <= MAX_TRACKED_EVENT_KEYS) return;

  const oldestKey = keys.values().next().value;

  if (oldestKey !== undefined) keys.delete(oldestKey);
}

function normalizeVietnameseTrackingText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLocaleLowerCase('vi-VN')
    .trim();
}

function getIsNewJob(createdAt: string): boolean | undefined {
  const createdAtTime = Date.parse(createdAt);

  if (Number.isNaN(createdAtTime)) return undefined;

  const jobAge = Date.now() - createdAtTime;

  return jobAge >= 0 && jobAge < NEW_JOB_MAX_AGE_MS;
}

export function getEventTrackingProperties(
  eventName: OpportunityTrackingEventName,
  properties: TrackingInfo | undefined
): TrackingInfo {
  const allowedNames = TRACKING_PROPERTY_NAMES[eventName];

  return Object.fromEntries(
    Object.entries(properties ?? {}).filter(([name]) => allowedNames.has(name))
  );
}

export function getApplicantTrackingProperties(job: JobSummary): TrackingInfo {
  const applicantSignal = job.applicantSignal?.value
    ?.trim()
    .toLocaleLowerCase('vi-VN');
  const topMatch = applicantSignal?.match(/^top(\d+)$/);
  const topApplicantCount = topMatch?.[1] ? Number(topMatch[1]) : undefined;
  const hasFewApplicant =
    applicantSignal === 'first' ||
    (topApplicantCount !== undefined && topApplicantCount <= 20);
  const isNewJob = getIsNewJob(job.createdAt);
  const applicantBadge = normalizeVietnameseTrackingText(
    job.applicantSignal?.badge || 'no_badge'
  );

  return {
    has_few_applicant: hasFewApplicant,
    ...(isNewJob !== undefined ? { is_new_job: isNewJob } : {}),
    text_on_applicant_badge: applicantBadge,
  };
}

export function getQuickResponseTrackingProperties(
  job: JobSummary
): TrackingInfo {
  const badgeType =
    job.badge?.content &&
    (job.badge.type === 'quick' || job.badge.type === 'normal')
      ? job.badge.type
      : 'no_badge';
  const numberOfDaysMatch = job.badge?.content?.match(/\d+/);
  const numberOfDays = numberOfDaysMatch?.[0]
    ? Number(numberOfDaysMatch[0])
    : undefined;

  return {
    has_quick_response_badge: badgeType !== 'no_badge',
    quick_response_badge_type: badgeType,
    ...(badgeType !== 'no_badge' && numberOfDays !== undefined
      ? { number_of_days_on_quick_response_badge: numberOfDays }
      : {}),
  };
}

export function getJobImpressionTrackingProperties(
  job: JobSummary
): TrackingInfo {
  const location = job.workingLocations.filter(Boolean).join(', ');

  return {
    is_salary_hidden:
      !job.prettySalary || /không công khai/i.test(job.prettySalary),
    job_title: job.jobTitle,
    matching_percentage: NO_PROFILE_GAP_MATCHING_PERCENTAGE,
    ...(job.jobLevel ? { job_level: job.jobLevel } : {}),
    ...(location ? { location } : {}),
    ...getQuickResponseTrackingProperties(job),
  };
}

export function getSaveOpportunityTrackingProperties(
  sourceScreen: SourceScreen
): TrackingInfo {
  return {
    library: 'web',
    matching_percentage: NO_PROFILE_GAP_MATCHING_PERCENTAGE,
    source_screen: sourceScreen,
  };
}

export function getApplicationStatusTrackingProperties(
  job: AppliedJob
): TrackingInfo {
  return {
    ...(job.latestStatus
      ? { application_latest_status: job.latestStatus }
      : {}),
    job_source: job.jobSource ?? 'upzi',
  };
}

export function getAskResultButtonTrackingProperties(
  job: AppliedJob
): TrackingInfo {
  return {
    ...getApplicationStatusTrackingProperties(job),
    ask_button_status:
      job.askResult?.enable === false
        ? job.askResult.buttonText
            ?.toLocaleLowerCase('vi-VN')
            .includes('đã hỏi')
          ? 'asked'
          : 'disable'
        : 'enable',
  };
}

export function getCvAttachmentTrackingValue(fileType?: number) {
  return fileType === 2 ? 'upzi_generated_cv' : 'uploaded_cv';
}

export function getSearchTrackingProperties(
  query: string,
  searchMethod?: SearchMethod
): TrackingInfo {
  const searchQuery = query.trim();

  return {
    ...(searchQuery && searchMethod
      ? { search_method: searchMethod }
      : { search_method: '' }),
    search_query: searchQuery,
  };
}

function getSelectedFilterNames(
  selectedIds: string[],
  options: JobFilterOption[] | undefined
): string[] {
  const selectedIdSet = new Set(selectedIds.map(String));

  return (
    options
      ?.filter(option => selectedIdSet.has(String(option.id)))
      .map(option => option.value) ?? []
  );
}

function getSelectedFilterName(
  selectedId: string,
  options: JobFilterOption[] | undefined
): string | undefined {
  return options?.find(option => String(option.id) === selectedId)?.value;
}

export function getFilterTrackingProperties(
  filters: JobFilterState,
  filterOptions: Partial<JobFilters>
): TrackingInfo {
  const location = getSelectedFilterNames(
    filters.location,
    filterOptions.location
  );
  const salary = getSelectedFilterNames(
    filters.salaryType,
    filterOptions.salaryType
  );
  const workingArrangement = getSelectedFilterNames(
    filters.workingArrangement,
    filterOptions.workingArrangement
  );
  const workingType = getSelectedFilterNames(
    filters.employmentType,
    filterOptions.employmentType
  );
  const weekendHoliday = getSelectedFilterNames(
    filters.workingOnWeekend,
    filterOptions.workingOnWeekend
  );
  const level = getSelectedFilterNames(
    filters.jobLevel,
    filterOptions.jobLevel
  );
  const fewApplicant = getSelectedFilterName(
    filters.numOfApplications,
    filterOptions.numOfApplications
  );
  const newJob = getSelectedFilterName(
    filters.postedWithin,
    filterOptions.postedWithin
  );

  return {
    ...(location.length ? { filter_location: location } : {}),
    ...(salary.length ? { filter_salary: salary } : {}),
    ...(workingArrangement.length
      ? { filter_working_arrangement: workingArrangement }
      : {}),
    ...(workingType.length ? { filter_working_type: workingType } : {}),
    ...(weekendHoliday.length
      ? { filter_weekend_holiday: weekendHoliday }
      : {}),
    ...(level.length ? { filter_level: level } : {}),
    ...(fewApplicant ? { filter_few_applicant: fewApplicant } : {}),
    ...(newJob ? { filter_new_job: newJob } : {}),
  };
}

export function useOpportunityTracking() {
  const { track } = usePosthog();

  const trackOpportunityEvent = useCallback(
    async ({
      applicationId,
      eventName,
      includeUserInfo = true,
      jobId,
      properties,
    }: TrackOpportunityEventOptions) => {
      const requirements = TRACKING_INFO_REQUIREMENTS[eventName];
      const [userInfo, opportunityInfo, applicationInfo] = await Promise.all([
        requirements.user && includeUserInfo
          ? getTrackingUserInfo()
          : Promise.resolve(null),
        requirements.opportunity && jobId
          ? getOpportunityTrackingInfo(jobId)
          : Promise.resolve(null),
        requirements.application && applicationId
          ? getApplicationTrackingInfo(applicationId)
          : Promise.resolve(null),
      ]);

      track(eventName, {
        ...(userInfo ? { user_info: userInfo } : {}),
        ...(opportunityInfo ? { opportunity_info: opportunityInfo } : {}),
        ...(applicationInfo ? { application_info: applicationInfo } : {}),
        ...getEventTrackingProperties(eventName, properties),
      });
    },
    [track]
  );

  return { trackOpportunityEvent };
}

export function useOpportunityImpression(
  job: JobSummary,
  {
    searchMethod,
    searchQuery,
    sourceScreen,
  }: {
    searchMethod?: SearchMethod;
    searchQuery: string;
    sourceScreen: SourceScreen;
  }
): RefCallback<HTMLElement> {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const elementRef = useRef<HTMLElement | null>(null);

  const setElementRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    const impressionKey = `${sourceScreen}:${job.id}:${searchQuery}:${searchMethod ?? ''}`;

    if (!element || trackedImpressions.has(impressionKey)) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopObserving = () => {};

    stopObserving = observeFullyVisible(element, isFullyVisible => {
      if (!isFullyVisible) {
        if (timer) clearTimeout(timer);
        timer = null;

        return;
      }

      if (timer) return;

      timer = setTimeout(() => {
        timer = null;
        addBoundedTrackingKey(trackedImpressions, impressionKey);
        void trackOpportunityEvent({
          eventName: OPPORTUNITY_TRACKING_EVENTS.impression,
          jobId: job.id,
          properties: {
            ...getJobImpressionTrackingProperties(job),
            opportunity_type: 'job',
            ...getSearchTrackingProperties(searchQuery, searchMethod),
            source_screen: sourceScreen,
          },
        });
        stopObserving();
      }, 3000);
    });

    return () => {
      stopObserving();
      if (timer) clearTimeout(timer);
    };
  }, [job, searchMethod, searchQuery, sourceScreen, trackOpportunityEvent]);

  return setElementRef;
}

export function useAskResultButtonImpression(
  job: AppliedJob
): RefCallback<HTMLElement> {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const elementRef = useRef<HTMLElement | null>(null);

  const setElementRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || trackedAskResultButtonImpressions.has(job.applicationId)) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopObserving = () => {};

    stopObserving = observeFullyVisible(element, isFullyVisible => {
      if (!isFullyVisible) {
        if (timer) clearTimeout(timer);
        timer = null;

        return;
      }

      if (timer) return;

      timer = setTimeout(() => {
        timer = null;
        addBoundedTrackingKey(
          trackedAskResultButtonImpressions,
          job.applicationId
        );
        void trackOpportunityEvent({
          applicationId: job.applicationId,
          eventName: OPPORTUNITY_TRACKING_EVENTS.askResultButtonImpression,
          jobId: job.jobId,
          properties: getAskResultButtonTrackingProperties(job),
        });
        stopObserving();
      }, 1000);
    });

    return () => {
      stopObserving();
      if (timer) clearTimeout(timer);
    };
  }, [job, trackOpportunityEvent]);

  return setElementRef;
}

export function useTrackOpportunityDetail(
  job: JobDetail,
  source: OpportunityDetailSource,
  includeUserInfo = true
) {
  const { trackOpportunityEvent } = useOpportunityTracking();

  useEffect(() => {
    const detailKey = `${source}:${job.id}`;

    if (trackedDetails.has(detailKey)) return;
    //TODO: tracking source_screen
    let sourceScreen: string = '';

    try {
      if (document.referrer) {
        const referrerUrl = new URL(document.referrer);

        if (referrerUrl.origin === window.location.origin) {
          sourceScreen = getWebsiteTrackingScreen(
            referrerUrl.pathname,
            referrerUrl.searchParams
          );
        }
      }
    } catch {
      // Keep the source-based fallback when the referrer is invalid.
    }

    addBoundedTrackingKey(trackedDetails, detailKey);
    void trackOpportunityEvent({
      eventName: OPPORTUNITY_TRACKING_EVENTS.viewDetail,
      includeUserInfo,
      jobId: job.id,
      properties: {
        ...getApplicantTrackingProperties(job),
        ...getQuickResponseTrackingProperties(job),
        has_hiring_line: false,
        has_profile_gap_result: false,
        has_profile_gap_trigger_button: false,
        opportunity_type: 'job',
        source_screen: sourceScreen,
      },
    });
  }, [includeUserInfo, job, source, trackOpportunityEvent]);
}
